import { useCallback, useMemo } from "react";
import { type Components, Virtuoso, type VirtuosoProps } from "react-virtuoso";

import { BdsLink } from "@buildertrend/components";

import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { type JobPickerJob } from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import { JobPickerEmptyStateBanner } from "commonComponents/utilities/JobPicker/JobPickerEmptyState/JobPickerEmptyStateBanner";
import { JobListItem } from "commonComponents/utilities/JobPicker/Sections/JobListItem/JobListItem";
import {
    ALL_JOBS,
    type IJobPickerJobsListProps,
    type IJobsListVirtualScrollProps,
    type IVirtualScrollContext,
} from "commonComponents/utilities/JobPicker/Sections/JobsList/JobsList.types";

function VirtualScrollFooter({ context }: { context?: IVirtualScrollContext }) {
    if (!context?.hasMoreJobs) {
        return null;
    }

    return (
        <div className="flex flex-row items-center justify-between py-4 px-6 text-sm font-normal text-fg-secondary">
            Showing 100 of {context?.count} jobs
            <BdsLink testid="ShowAll" onClick={context.onShowMoreThanOneHundred}>
                View more
            </BdsLink>
        </div>
    );
}

const virtualScrollComponents: Components<JobPickerJob, IVirtualScrollContext> = {
    Footer: VirtualScrollFooter,
};

export function JobPickerJobsList({
    listMetadata,
    jobsListAndCount,
    loading,
    clearKeywordSearch,
    templatesOnly,
    isConnectedToAccounting,
    jobs,
    availableAccounts,
    onShowMoreThanOneHundred,
    keywordSearch,
    showAccountingOnTimeClock,
    draftJobIds,
    displayMode,
    onJobPickerJobChanged,
    isTemplateMode,
    selectedJobIds,
}: IJobPickerJobsListProps) {
    if (loading || !jobsListAndCount) {
        return <BTLoading />;
    }

    const jobsList = jobsListAndCount.jobs;
    if (jobsList.find((x) => x.jobId !== ALL_JOBS) === undefined) {
        // If only job is "All Listed", then display empty
        const isEmptyDueToKeywordSearch = jobs.find((x) => x.jobId !== ALL_JOBS) !== undefined;
        return (
            <div id="JobPickerJobListEmptyState">
                <JobPickerEmptyStateBanner
                    isDismissed={listMetadata.emptyStatesMetadata.isNewToEntity}
                    actionBeingPerformed={undefined}
                    isNewToEntity={false}
                    hasListData={false}
                    hasFilteredData={true}
                    isReadonly={listMetadata.emptyStatesMetadata.isReadOnly}
                    onCallToActionClick={clearKeywordSearch}
                    canClearSearch={isEmptyDueToKeywordSearch}
                    isBanner={false}
                    mode={templatesOnly ? "Template" : "Job"}
                    isConnectedToAccounting={isConnectedToAccounting}
                />
            </div>
        );
    }

    const hasMoreJobs = jobsListAndCount.count !== jobsListAndCount.jobs.length;

    return (
        <JobsListVirtualScroll
            availableAccounts={availableAccounts}
            hasMoreJobs={hasMoreJobs}
            displayMode={displayMode}
            draftJobIds={draftJobIds}
            isTemplateMode={isTemplateMode}
            keywordSearch={keywordSearch}
            onJobPickerJobChanged={onJobPickerJobChanged}
            onShowMoreThanOneHundred={onShowMoreThanOneHundred}
            selectedJobIds={selectedJobIds}
            showAccountingOnTimeClock={showAccountingOnTimeClock}
            jobsListAndCount={jobsListAndCount}
        />
    );
}

function JobsListVirtualScroll({
    jobsListAndCount,
    hasMoreJobs,
    onShowMoreThanOneHundred,
    selectedJobIds,
    availableAccounts,
    displayMode,
    draftJobIds,
    keywordSearch,
    onJobPickerJobChanged,
    showAccountingOnTimeClock,
    isTemplateMode,
}: IJobsListVirtualScrollProps) {
    const context: IVirtualScrollContext = useMemo(
        () => ({
            hasMoreJobs,
            onShowMoreThanOneHundred,
            selectedJobIds,
            count: jobsListAndCount.count,
        }),
        [hasMoreJobs, onShowMoreThanOneHundred, selectedJobIds, jobsListAndCount.count],
    );

    const renderItem: VirtuosoProps<JobPickerJob, IVirtualScrollContext>["itemContent"] =
        useCallback(
            (_index: number, job: JobPickerJob) => (
                <JobListItem
                    currentJob={job}
                    availableAccounts={availableAccounts}
                    displayMode={displayMode}
                    isTemplateMode={isTemplateMode}
                    onJobPickerJobChanged={onJobPickerJobChanged}
                    selectedJobIds={selectedJobIds}
                    showAccountingOnTimeClock={showAccountingOnTimeClock}
                    draftJobIds={draftJobIds}
                    keywordSearch={keywordSearch}
                />
            ),
            [
                selectedJobIds,
                keywordSearch,
                displayMode,
                draftJobIds,
                isTemplateMode,
                onJobPickerJobChanged,
                showAccountingOnTimeClock,
                availableAccounts,
            ],
        );

    return (
        <Virtuoso
            className="JobList p-0 flex-1 overflow-y-auto overflow-x-hidden"
            data-testid="jobpicker-jobslist-virtualscroll"
            data={jobsListAndCount.jobs}
            components={virtualScrollComponents}
            context={context}
            increaseViewportBy={200}
            computeItemKey={(_index, job) => job.jobId}
            itemContent={renderItem}
        />
    );
}
